import { useMemo, useRef, useState } from "react";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  UploadSimple, DownloadSimple, ArrowsClockwise,
  CheckCircle, WarningCircle, MinusCircle, X, Files, ShoppingCart, FileXls,
} from "@phosphor-icons/react";

const money = (n) =>
  (n == null || n === "" || isNaN(Number(n)))
    ? "—"
    : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function toCsvCell(v) {
  const s = String(v ?? "").replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
}
function downloadCsv(header, rows, filename) {
  const lines = [header.join(",")];
  for (const r of rows) lines.push(r.map(toCsvCell).join(","));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

/* -------------------------- Page shell + tabs -------------------------- */

export default function SalesFile() {
  const [section, setSection] = useState("sales"); // "sales" | "purchase"
  return (
    <div>
      <PageHeader title="File Match" subtitle="Reconcile Sales files or match Purchase files across systems" />
      <Section>
        <div className="inline-flex bg-white border border-slate-200 rounded-sm overflow-hidden mb-4" role="tablist">
          <button
            role="tab" data-testid="sf-tab-sales"
            aria-selected={section === "sales"}
            onClick={()=>setSection("sales")}
            className={`px-4 py-2 text-sm font-semibold border-r border-slate-200 ${section==="sales" ? "bg-orange-600 text-white" : "text-slate-700 hover:bg-slate-50"}`}
          ><Files size={14} className="inline mr-1"/> Reconcile Sales Files
            <div className="text-[10px] font-normal opacity-80">3 files · Shoper / QB / Tally</div>
          </button>
          <button
            role="tab" data-testid="sf-tab-purchase"
            aria-selected={section === "purchase"}
            onClick={()=>setSection("purchase")}
            className={`px-4 py-2 text-sm font-semibold ${section==="purchase" ? "bg-slate-800 text-white" : "text-slate-700 hover:bg-slate-50"}`}
          ><ShoppingCart size={14} className="inline mr-1"/> Match Purchase Files
            <div className="text-[10px] font-normal opacity-80">3 files · Supplier Invoice No.</div>
          </button>
        </div>

        {section === "sales" ? <ReconcileSales /> : <PurchaseMatch />}
      </Section>
    </div>
  );
}

/* -------------------------- Section 1: Sales 3-way reconcile (Shoper vs QB vs Tally) -------------------------- */

const SALES_STATUS = {
  matched:        { label: "Matched (all 3)",       cls: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: CheckCircle },
  matched_partial:{ label: "Matched (partial)",     cls: "text-amber-800   bg-amber-50   border-amber-200",   Icon: WarningCircle },
  shoper_only:    { label: "Only in Shoper",        cls: "text-rose-700    bg-rose-50    border-rose-200",    Icon: MinusCircle },
  qb_tally_only:  { label: "Only in QB/Tally",      cls: "text-blue-700    bg-blue-50    border-blue-200",    Icon: MinusCircle },
};

const rowStatus = (r) => {
  if (r.match_type?.startsWith("Matched (cross")) return "matched";
  if (r.match_type?.startsWith("Matched"))        return "matched_partial";
  if (r.match_type?.includes("Shoper only"))      return "shoper_only";
  return "qb_tally_only";
};

function ReconcileSales() {
  const [shoper, setShoper] = useState(null);
  const [qb, setQb]         = useState(null);
  const [tally, setTally]   = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [anomOnly, setAnomOnly] = useState(false);
  const [q, setQ] = useState("");
  const inputSh = useRef(null), inputQb = useRef(null), inputTa = useRef(null);

  const filesCount = [shoper, qb, tally].filter(Boolean).length;
  const rows = result?.rows || [];
  const summary = result?.summary;
  const meta = result?.meta;

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter(r => {
      const st = rowStatus(r);
      if (statusFilter !== "all" && st !== statusFilter) return false;
      if (anomOnly && r.anomaly !== "YES") return false;
      if (!s) return true;
      return (r.bill_no_shoper || "").toLowerCase().includes(s) ||
             (r.voucher_no_qb || "").toLowerCase().includes(s) ||
             (r.voucher_no_tally || "").toLowerCase().includes(s) ||
             (r.party_shoper || "").toLowerCase().includes(s) ||
             (r.party_qb || "").toLowerCase().includes(s) ||
             (r.particulars_tally || "").toLowerCase().includes(s);
    });
  }, [rows, statusFilter, anomOnly, q]);

  const buildForm = () => {
    const form = new FormData();
    if (shoper) form.append("shoper", shoper);
    if (qb)     form.append("qb", qb);
    if (tally)  form.append("tally", tally);
    return form;
  };

  const reconcile = async () => {
    if (filesCount < 2) return toast.error("Please upload at least 2 of Shoper / QB / Tally");
    if (filesCount === 2) toast.warning("Only 2 files uploaded — running in degraded mode");
    setLoading(true);
    try {
      const { data } = await client.post("/sales-file/reconcile-3way", buildForm(),
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Reconciled — ${data.summary.total_anomalies} anomalies detected`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const exportXlsx = async () => {
    if (filesCount < 2) return;
    setExporting(true);
    try {
      const res = await client.post("/sales-file/reconcile-3way?format=xlsx", buildForm(),
        { headers: { "Content-Type": "multipart/form-data" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sales_Reconciliation_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(false); }
  };

  const reset = () => {
    setShoper(null); setQb(null); setTally(null); setResult(null);
    if (inputSh.current) inputSh.current.value = "";
    if (inputQb.current) inputQb.current.value = "";
    if (inputTa.current) inputTa.current.value = "";
  };

  return (
    <div>
      {!result ? (
        <>
          <div className="grid gap-4 md:grid-cols-3 mb-4">
            <UploadCard label="Shoper File"  hint="Bill No. · Doc. Date · Bill Amount · Qty" file={shoper} onPick={setShoper} inputRef={inputSh} testid="salesfile-input-shoper" sampleHref="/samples/sales-file-sample-shoper.xlsx"/>
            <UploadCard label="QB File"      hint="Voucher Number · Party · Net Amount"     file={qb}     onPick={setQb}     inputRef={inputQb} testid="salesfile-input-qb"     sampleHref="/samples/sales-file-sample-qb.xlsx"/>
            <UploadCard label="Tally File"   hint="Voucher No. · Particulars · Gross Total" file={tally}  onPick={setTally}  inputRef={inputTa} testid="salesfile-input-tally"  sampleHref="/samples/sales-file-sample-tally.xlsx"/>
          </div>
          <div className="mb-3 text-xs bg-blue-50 border border-blue-200 text-blue-900 px-3 py-2 rounded-sm flex items-start gap-2" data-testid="salesfile-samples-note">
            <FileXls size={14} weight="bold" className="mt-0.5 shrink-0"/>
            <div>
              First time here? <b>Download the three sample files</b> above (each card has a "Sample" link) and reconcile them —
              the result includes every kind of match, tolerance-diff match, and anomaly so you can see the report before you
              upload your live exports.
            </div>
          </div>
          <div className="text-xs text-slate-500 mb-3 space-y-0.5">
            <div>• QB ↔ Tally match on <b>Voucher No.</b> · Shoper matches QB/Tally on <b>Date + Qty + Amount</b>.</div>
            <div>• Amount differences ≤ <b>₹2.00</b> are treated as a match (rounding / GST noise).</div>
            <div>• At least 2 files required. If only 2 are provided, a warning will be shown.</div>
          </div>
          {filesCount === 2 && (
            <div className="mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-sm" data-testid="salesfile-warn-2files">
              Only 2 files selected — reconciliation will run but the missing file's columns will be blank.
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={reconcile} disabled={filesCount < 2 || loading}
                    className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm" data-testid="salesfile-reconcile-btn">
              <ArrowsClockwise size={16} className="mr-1" weight="bold"/>{loading ? "Reconciling…" : `Reconcile (${filesCount}/3 files)`}
            </Button>
          </div>
        </>
      ) : (
        <div className="space-y-6">
          {summary.warning && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-sm" data-testid="salesfile-summary-warning">
              {summary.warning}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 flex-1" data-testid="salesfile-summary">
              <StatTile label="Shoper records"    value={summary.shoper_records}   accent="border-slate-400"/>
              <StatTile label="QB records"        value={summary.qb_records}       accent="border-slate-400"/>
              <StatTile label="Tally records"     value={summary.tally_records}    accent="border-slate-400"/>
              <StatTile label="QB ↔ Tally match"  value={summary.qb_tally_exact}   accent="border-blue-500"/>
              <StatTile label="All 3 matched"     value={summary.cross_referenced} accent="border-emerald-500" clickable onClick={()=>{setStatusFilter("matched");setAnomOnly(false);}}/>
              <StatTile label="Total anomalies"   value={summary.total_anomalies}  accent="border-red-600"     clickable onClick={()=>{setAnomOnly(true);setStatusFilter("all");}} testid="salesfile-anomaly-tile"/>
            </div>
            <div className="flex gap-2">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" onClick={exportXlsx} disabled={exporting} data-testid="salesfile-export-xlsx-btn">
                <DownloadSimple size={14} className="mr-1"/>{exporting ? "Exporting…" : "Excel"}
              </Button>
              <Button variant="outline" className="rounded-sm" onClick={reset} data-testid="salesfile-reset-btn"><X size={14} className="mr-1"/> Clear</Button>
            </div>
          </div>

          <details className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-3">
            <summary className="cursor-pointer font-semibold">Detected columns & sheets</summary>
            <div className="grid md:grid-cols-3 gap-4 mt-3">
              {meta.shoper && <MetaBlock title="Shoper" m={meta.shoper}/>}
              {meta.qb     && <MetaBlock title="QB"     m={meta.qb}/>}
              {meta.tally  && <MetaBlock title="Tally"  m={meta.tally}/>}
            </div>
          </details>

          <div className="flex flex-wrap items-center gap-2">
            <Input data-testid="salesfile-search" placeholder="Search bill/voucher/party…" value={q} onChange={(e)=>setQ(e.target.value)} className="max-w-xs rounded-sm"/>
            <div className="flex flex-wrap gap-1">
              {["all", ...Object.keys(SALES_STATUS)].map(s => (
                <button key={s} data-testid={`salesfile-filter-${s}`} onClick={()=>{setStatusFilter(s);setAnomOnly(false);}}
                        className={`text-xs px-3 py-1.5 rounded-sm border font-semibold ${statusFilter === s && !anomOnly ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                  {s === "all" ? "All" : SALES_STATUS[s]?.label}
                </button>
              ))}
              <button data-testid="salesfile-filter-anomaly" onClick={()=>{setAnomOnly(v=>!v);setStatusFilter("all");}}
                      className={`text-xs px-3 py-1.5 rounded-sm border font-semibold ${anomOnly ? "bg-red-600 text-white border-red-600" : "bg-white text-red-700 border-red-300 hover:bg-red-50"}`}>
                {anomOnly ? "✓ " : ""}Anomalies only
              </button>
            </div>
            <div className="ml-auto text-xs text-slate-500">Showing <b>{filtered.length}</b> of {rows.length}</div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
                <th className="px-2 py-3 whitespace-nowrap">Date</th>
                <th className="px-2 py-3 whitespace-nowrap bg-rose-50/60">Shoper Bill</th>
                <th className="px-2 py-3 whitespace-nowrap bg-blue-50/60">QB Voucher</th>
                <th className="px-2 py-3 whitespace-nowrap bg-amber-50/60">Tally Voucher</th>
                <th className="px-2 py-3">Party</th>
                <th className="px-2 py-3 text-right">Qty S/Q</th>
                <th className="px-2 py-3 text-right bg-rose-50/60">Shoper Amt</th>
                <th className="px-2 py-3 text-right bg-blue-50/60">QB Amt</th>
                <th className="px-2 py-3 text-right bg-amber-50/60">Tally Amt</th>
                <th className="px-2 py-3 text-right">Diff</th>
                <th className="px-2 py-3">Match</th>
                <th className="px-2 py-3">Reason</th>
              </tr></thead>
              <tbody data-testid="salesfile-tbody">
                {filtered.map((r, i) => {
                  const st = SALES_STATUS[rowStatus(r)]; const I = st.Icon;
                  const rowKey = `${r.bill_no_shoper}|${r.voucher_no_qb}|${r.voucher_no_tally}|${i}`;
                  const anom = r.anomaly === "YES";
                  const party = r.party_shoper || r.party_qb || r.particulars_tally || "—";
                  return (
                    <tr key={rowKey} data-testid={`salesfile-row-${i}`} className={`border-b border-slate-100 ${anom ? "bg-red-50/60 hover:bg-red-50" : "hover:bg-slate-50"}`}>
                      <td className="px-2 py-2 font-mono whitespace-nowrap">{r.date || "—"}</td>
                      <td className="px-2 py-2 font-mono font-semibold bg-rose-50/20">{r.bill_no_shoper || "—"}</td>
                      <td className="px-2 py-2 font-mono font-semibold bg-blue-50/20">{r.voucher_no_qb || "—"}</td>
                      <td className="px-2 py-2 font-mono font-semibold bg-amber-50/20">{r.voucher_no_tally || "—"}</td>
                      <td className="px-2 py-2 max-w-[220px] truncate" title={party}>{party}</td>
                      <td className="px-2 py-2 text-right font-mono">{r.qty_shoper == null ? "—" : money(r.qty_shoper)}/{r.qty_qb == null ? "—" : money(r.qty_qb)}</td>
                      <td className="px-2 py-2 text-right font-mono bg-rose-50/20">{money(r.bill_amt_shoper)}</td>
                      <td className="px-2 py-2 text-right font-mono bg-blue-50/20">{money(r.net_amt_qb)}</td>
                      <td className="px-2 py-2 text-right font-mono bg-amber-50/20">{money(r.gross_total_tally)}</td>
                      <td className={`px-2 py-2 text-right font-mono font-semibold ${anom ? "text-red-700" : "text-emerald-700"}`}>{r.amount_diff == null ? "—" : money(r.amount_diff)}</td>
                      <td className="px-2 py-2"><span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-sm border ${st.cls}`}><I size={12} weight="fill"/> {st.label}</span></td>
                      <td className="px-2 py-2 text-xs text-slate-600 max-w-[240px] truncate" title={r.anomaly_reason}>{r.anomaly_reason || "—"}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-500">No rows match this filter</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------- Section 2: Purchase 3-way match -------------------------- */

const P_STATUS = {
  all3:          { label: "All 3 match",         cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  tally_qb:      { label: "Tally + QB",          cls: "text-blue-700    bg-blue-50    border-blue-200" },
  tally_shopper: { label: "Tally + Shopper",     cls: "text-indigo-700  bg-indigo-50  border-indigo-200" },
  qb_shopper:    { label: "QB + Shopper",        cls: "text-violet-700  bg-violet-50  border-violet-200" },
  tally_only:    { label: "Only in Tally",       cls: "text-amber-800   bg-amber-50   border-amber-200" },
  qb_only:       { label: "Only in QB",          cls: "text-rose-700    bg-rose-50    border-rose-200" },
  shopper_only:  { label: "Only in Shopper",     cls: "text-cyan-800    bg-cyan-50    border-cyan-200" },
};

function PurchaseMatch() {
  const [tally, setTally]     = useState(null);
  const [qb, setQb]           = useState(null);
  const [shopper, setShopper] = useState(null);
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [gstOnly, setGstOnly] = useState(false);
  const [q, setQ] = useState("");
  const inputT = useRef(null), inputQ = useRef(null), inputS = useRef(null);

  const cols = result?.columns;
  const rows = result?.rows || [];
  const meta = result?.meta;
  const summary = result?.summary;
  const displayCols = useMemo(
    () => (cols ? [...cols.tally, ...cols.qb, ...cols.shopper] : []),
    [cols]
  );

  // Detect the tax columns (case/whitespace tolerant). Falls back to the plain names.
  const taxKeys = useMemo(() => {
    if (!cols) return { qb: null, shopper: null };
    const findKey = (arr, needle) => {
      const n = needle.toLowerCase();
      return arr.find(c => c.toLowerCase().includes(n)) || null;
    };
    return {
      qb:      findKey(cols.qb || [], "tax"),
      shopper: findKey(cols.shopper || [], "tax"),
    };
  }, [cols]);

  const toNum = (v) => {
    const n = Number(String(v ?? "").replace(/,/g, "").trim());
    return Number.isFinite(n) ? n : NaN;
  };

  // GST amount tolerance — anything within ±₹2.10 is treated as a match (rounding/GST noise).
  const GST_TOLERANCE = 2.10;

  // Per-row GST diff: only meaningful when BOTH files have a value.
  const gstFor = (r) => {
    if (!taxKeys.qb || !taxKeys.shopper) return { has: false, mismatch: false, diff: 0 };
    const a = toNum(r[taxKeys.qb]);
    const b = toNum(r[taxKeys.shopper]);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return { has: false, mismatch: false, diff: 0 };
    const diff = Math.round((a - b) * 100) / 100;
    return { has: true, mismatch: Math.abs(diff) > GST_TOLERANCE, diff, a, b };
  };

  const gstMismatchCount = useMemo(
    () => rows.reduce((n, r) => n + (gstFor(r).mismatch ? 1 : 0), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, taxKeys]
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (gstOnly && !gstFor(r).mismatch) return false;
      if (!s) return true;
      if ((r.voucher_no || "").toLowerCase().includes(s)) return true;
      return displayCols.some(c => String(r[c] || "").toLowerCase().includes(s));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, statusFilter, gstOnly, q, displayCols, taxKeys]);

  const submit = async () => {
    if (!tally || !qb || !shopper) return toast.error("Please pick all 3 files (Tally, QB, Shopper)");
    const form = new FormData();
    form.append("tally", tally); form.append("qb", qb); form.append("shopper", shopper);
    setLoading(true);
    try {
      const { data } = await client.post("/sales-file/match-purchase", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Matched — ${data.summary.total_keys} invoices across 3 files`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const reset = () => {
    setTally(null); setQb(null); setShopper(null); setResult(null);
    if (inputT.current) inputT.current.value = "";
    if (inputQ.current) inputQ.current.value = "";
    if (inputS.current) inputS.current.value = "";
  };

  const doExport = () => {
    if (!filtered.length) return;
    const header = ["Invoice Ref", "Status", "GST Mismatch", "GST Diff (QB−Shopper)", ...displayCols];
    const rowsCsv = filtered.map(r => {
      const g = gstFor(r);
      return [r.voucher_no, r.status, g.has ? (g.mismatch ? "YES" : "no") : "", g.has ? g.diff : "",
              ...displayCols.map(c => r[c] ?? "")];
    });
    downloadCsv(header, rowsCsv, "purchase-3-way-match.csv");
  };

  return (
    <div>
      {!result ? (
        <>
          <div className="grid gap-4 md:grid-cols-3 mb-6">
            <UploadCard label="Tally File"    hint="Purchase Register · Supplier Invoice No." accept=".xlsx,.xls" file={tally}   onPick={setTally}   inputRef={inputT} testid="pm-input-tally"/>
            <UploadCard label="QB File"       hint="Ref. No column"                            accept=".xlsx,.xls" file={qb}      onPick={setQb}      inputRef={inputQ} testid="pm-input-qb"/>
            <UploadCard label="Shopper File"  hint="Dc. No. column"                            accept=".xlsx,.xls" file={shopper} onPick={setShopper} inputRef={inputS} testid="pm-input-shopper"/>
          </div>
          <div className="text-xs text-slate-500 mb-3">
            Matched on <b>Supplier Invoice No.</b> (Tally) ↔ <b>Ref. No</b> (QB) ↔ <b>Dc. No.</b> (Shopper). Punctuation and spacing are ignored, so <code className="font-mono">291F1100002952</code> matches <code className="font-mono">291F-1100-002952</code>.
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={!tally || !qb || !shopper || loading}
                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" data-testid="pm-submit-btn">
              <ArrowsClockwise size={16} className="mr-1" weight="bold"/>{loading ? "Matching…" : "Match all 3 files"}
            </Button>
          </div>
        </>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 flex-1" data-testid="pm-summary">
              <StatTile label="Total invoices"       value={summary.total_keys}     accent="border-slate-400"/>
              <StatTile label="All 3 match"          value={summary.all3}           accent="border-emerald-500" clickable onClick={()=>{setStatusFilter("all3");setGstOnly(false);}}/>
              <StatTile label="Tally + QB"           value={summary.tally_qb}       accent="border-blue-500"    clickable onClick={()=>{setStatusFilter("tally_qb");setGstOnly(false);}}/>
              <StatTile label="Tally + Shopper"      value={summary.tally_shopper}  accent="border-indigo-500"  clickable onClick={()=>{setStatusFilter("tally_shopper");setGstOnly(false);}}/>
              <StatTile label="QB + Shopper"         value={summary.qb_shopper}     accent="border-violet-500"  clickable onClick={()=>{setStatusFilter("qb_shopper");setGstOnly(false);}}/>
              <StatTile label="Only Tally"           value={summary.tally_only}     accent="border-amber-500"   clickable onClick={()=>{setStatusFilter("tally_only");setGstOnly(false);}}/>
              <StatTile label="Only QB / Only Shp."  value={summary.qb_only + summary.shopper_only} accent="border-rose-500" clickable onClick={()=>{setStatusFilter("qb_only");setGstOnly(false);}}/>
              <StatTile
                label="GST Mismatch"
                value={gstMismatchCount}
                accent={gstMismatchCount > 0 ? "border-red-600" : "border-slate-300"}
                clickable
                onClick={()=>{ setGstOnly(true); setStatusFilter("all"); }}
                testid="pm-gst-mismatch-tile"
              />
            </div>
            <Button variant="outline" className="rounded-sm" onClick={reset} data-testid="pm-reset-btn"><X size={14} className="mr-1"/> Clear</Button>
          </div>

          <details className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-3">
            <summary className="cursor-pointer font-semibold">Detected sheets, header rows, and key columns</summary>
            <div className="grid md:grid-cols-3 gap-4 mt-3">
              <MetaBlock title="Tally"   m={meta.tally}/>
              <MetaBlock title="QB"      m={meta.qb}/>
              <MetaBlock title="Shopper" m={meta.shopper}/>
            </div>
          </details>

          <div className="flex flex-wrap items-center gap-2">
            <Input data-testid="pm-search" placeholder="Search invoice ref, party, notes…" value={q} onChange={(e)=>setQ(e.target.value)} className="max-w-xs rounded-sm"/>
            <div className="flex flex-wrap gap-1">
              {["all", ...Object.keys(P_STATUS)].map(s => (
                <button key={s} data-testid={`pm-filter-${s}`} onClick={()=>setStatusFilter(s)}
                        className={`text-xs px-3 py-1.5 rounded-sm border font-semibold ${statusFilter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                  {s === "all" ? "All" : P_STATUS[s]?.label}
                </button>
              ))}
              <button
                data-testid="pm-filter-gst-mismatch"
                onClick={()=>setGstOnly(v => !v)}
                disabled={!taxKeys.qb || !taxKeys.shopper}
                title={(!taxKeys.qb || !taxKeys.shopper) ? "Tax columns not detected in one or both files" : "Show only rows where |QB Tax − Shopper Tax| > ₹2.10"}
                className={`text-xs px-3 py-1.5 rounded-sm border font-semibold ${gstOnly ? "bg-red-600 text-white border-red-600" : "bg-white text-red-700 border-red-300 hover:bg-red-50"} ${(!taxKeys.qb || !taxKeys.shopper) ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {gstOnly ? "✓ " : ""}GST Mismatch {gstMismatchCount > 0 && `(${gstMismatchCount})`}
              </button>
            </div>
            <div className="ml-auto text-xs text-slate-500">Showing <b>{filtered.length}</b> of {rows.length}</div>
            <Button variant="outline" className="rounded-sm" onClick={doExport} data-testid="pm-export-btn"><DownloadSimple size={14} className="mr-1"/> CSV</Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
                  <th className="px-2 py-3 sticky left-0 bg-slate-50 z-10 min-w-[160px]">Invoice Ref</th>
                  <th className="px-2 py-3 min-w-[130px]">Status</th>
                  {cols.tally.map(c => (
                    <th key={c} className="px-2 py-3 whitespace-nowrap bg-amber-50/60 border-l border-amber-100">{c.replace(/^tally_/, "T · ")}</th>
                  ))}
                  {cols.qb.map(c => (
                    <th key={c} className="px-2 py-3 whitespace-nowrap bg-blue-50/60 border-l border-blue-100">{c.replace(/^qb_/, "Q · ")}</th>
                  ))}
                  {cols.shopper.map(c => (
                    <th key={c} className="px-2 py-3 whitespace-nowrap bg-cyan-50/60 border-l border-cyan-100">{c.replace(/^shopper_/, "S · ")}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="pm-tbody">
                {filtered.map(r => {
                  const st = P_STATUS[r.status];
                  const g = gstFor(r);
                  const gstCellCls = "bg-red-100 text-red-800 font-bold ring-1 ring-inset ring-red-300";
                  return (
                    <tr
                      key={r.key_norm}
                      data-testid={`pm-row-${r.key_norm}`}
                      data-gst-mismatch={g.mismatch ? "1" : "0"}
                      className={`border-b border-slate-100 ${g.mismatch ? "bg-red-50/60 hover:bg-red-50" : "hover:bg-slate-50"}`}
                    >
                      <td className={`px-2 py-2 font-mono font-semibold sticky left-0 z-10 ${g.mismatch ? "bg-red-50/60" : "bg-white"}`}>
                        {g.mismatch && <span className="mr-1 inline-block h-2 w-2 rounded-full bg-red-600 align-middle" title={`QB Tax ${g.a} ≠ Shopper Tax ${g.b}`}/>}
                        {r.voucher_no}
                      </td>
                      <td className="px-2 py-2">
                        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-sm border ${st.cls}`}>{st.label}</span>
                        {g.mismatch && (
                          <span
                            data-testid="pm-row-gst-flag"
                            className="ml-1 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-sm border border-red-300 bg-red-100 text-red-800"
                            title={`Diff: ${g.diff}`}
                          >GST!</span>
                        )}
                      </td>
                      {cols.tally.map(c => <td key={c} className="px-2 py-2 whitespace-nowrap bg-amber-50/20 border-l border-amber-100/60">{r[c] || "—"}</td>)}
                      {cols.qb.map(c => {
                        const isTax = taxKeys.qb === c;
                        return (
                          <td key={c} className={`px-2 py-2 whitespace-nowrap border-l border-blue-100/60 ${isTax && g.mismatch ? gstCellCls : "bg-blue-50/20"}`}>
                            {r[c] || "—"}
                          </td>
                        );
                      })}
                      {cols.shopper.map(c => {
                        const isTax = taxKeys.shopper === c;
                        return (
                          <td key={c} className={`px-2 py-2 whitespace-nowrap border-l border-cyan-100/60 ${isTax && g.mismatch ? gstCellCls : "bg-cyan-50/20"}`}>
                            {r[c] || "—"}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={2 + displayCols.length} className="px-4 py-10 text-center text-slate-500">No vouchers match this filter</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------- Shared pieces -------------------------- */

function UploadCard({ label, hint, file, onPick, inputRef, testid, accept = ".xlsx,.xls", sampleHref = null }) {
  return (
    <div className="relative">
      <label className="block bg-white border-2 border-dashed border-slate-300 rounded-sm p-5 cursor-pointer hover:border-orange-500 hover:bg-orange-50/30 transition-colors">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-orange-100 text-orange-700 grid place-items-center shrink-0">
            <UploadSimple size={20} weight="bold"/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="label-cap">{label}</div>
            <div className="font-semibold text-sm truncate">{file ? file.name : "Click or drop a file here"}</div>
            <div className="text-xs text-slate-500 truncate">{hint}</div>
          </div>
        </div>
        <input ref={inputRef} type="file" accept={accept} className="hidden" data-testid={testid}
               onChange={(e) => onPick(e.target.files?.[0] || null)}/>
      </label>
      {sampleHref && (
        <a
          href={sampleHref}
          download
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testid}-sample`}
          className="absolute top-2 right-2 text-[11px] font-semibold text-blue-700 hover:text-blue-900 hover:underline bg-blue-50 border border-blue-200 rounded-sm px-2 py-0.5 inline-flex items-center gap-1 z-10"
          title={`Download a sample ${label}`}
        >
          <DownloadSimple size={11}/> Sample
        </a>
      )}
    </div>
  );
}

function StatTile({ label, value, accent = "border-slate-300", clickable, onClick, testid }) {
  const cls = `bg-white border-l-4 ${accent} rounded-sm p-3 ${clickable ? "cursor-pointer hover:shadow-sm" : ""}`;
  return (
    <div className={cls} onClick={clickable ? onClick : undefined} data-testid={testid}>
      <div className="label-cap truncate">{label}</div>
      <div className="text-xl font-black text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function MetaBlock({ title, m }) {
  return (
    <div>
      <div className="font-semibold mb-1">{title}</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
        <span className="text-slate-500">File</span>       <span className="truncate" title={m.filename}>{m.filename}</span>
        <span className="text-slate-500">Sheet</span>      <span>{m.sheet}</span>
        <span className="text-slate-500">Header row</span> <span>{m.header_row + 1}</span>
        <span className="text-slate-500">Key column</span> <span className="font-mono">{m.key_column}</span>
        {m.party_column  != null && (<><span className="text-slate-500">Party column</span> <span>{m.party_column || "—"}</span></>)}
        {m.date_column   != null && (<><span className="text-slate-500">Date column</span>  <span>{m.date_column  || "—"}</span></>)}
        {m.amount_column != null && (<><span className="text-slate-500">Amount column</span><span>{m.amount_column|| "—"}</span></>)}
        <span className="text-slate-500">Rows</span>       <span>{m.rows}</span>
      </div>
    </div>
  );
}
